<template>
  <div v-loading="dataLoading" class="app-content-wrapper">
    <div class="search-wrapper">
      <search-component
        ref="search"
        class="dispute-search"
        :search-condition="searchCondition"
        :search-buttons="searchButtons"
        :searchButtonMaxNum="3"
        :fuzzySearchPlaceholder="$t('message.alert.ethocaId')"
        :fuzzySearchKey="'ethocaId'"
        :isDisplayReset="true"
        router-name="/risk/ethoca/alerts/list"
      >
      </search-component>
    </div>
    <div class="table-wrapper">
      <custom-table
        :table-set="tableCondition"
        :service-function="queryEthocaAlertList"
        ref="table"
        :is-first-automatic-visit="false"
        router-name="/risk/ethoca/alerts/list"
      >
      </custom-table>
    </div>
    <validation-modal
      ref="outcomeModal"
      :titleText="$t('message.alert.outcome')"
      :validation-condition="outcomeCondition"
      :default-function="onOutcome"
      @refresh="refreshTable"></validation-modal>
    <validation-modal
      ref="batchModal"
      :titleText="$t('message.common.batchOperations')"
      :validation-condition="batchCondition"
      :default-function="onBatchOperation"
      :showSubmitMessage="false"></validation-modal>
    <UploadModal
      ref="uploadModal"
      :titleText="$t('message.common.import')"
      templateFileName="ethoca_alert_template.xlsx"
      templateFileNameShow="ONREWAY_Ethoca_Alert_Outcome_Sample.xlsx"
      :uploadFunc="onUploadFile"
      accept="excel">
    </UploadModal>
    <custom-message ref="customMessage" :closeOnClickModal="false" :closeOnPressEscape="false"></custom-message>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';

import UploadModal from '@/components/modal/base-modal/upload-modal.vue';
import ValidationModal from '@/components/modal/base-modal/validation-modal.vue';
import CustomMessage from '@/components/modal/message-box/custom-message.vue';
import { ShowSuccessMessage, ShowWarningMessage } from '@/components/modal/tips/message-tips.js';
import CustomTable from '@/components/table/custom-table.vue';
import SearchComponent from '@/components/table/search-component.vue';
import { REQUIRED } from '@/components/validation/tool/validator';
import GlobalData from '@/utils/global/index';
import {
  getEnantiomericValue, initSelectedData,
} from '@/utils/tools/constant';
import {
  queryEthocaAlertList, batchEthocaAlertOutcome,
  outcomeEthocaAlert, downloadEthocaAlertsList,
  batchImportEthocaAlerts,
} from '@/views/risk/api/ethoca';

export default {
  components: {
    SearchComponent,
    CustomTable,
    ValidationModal,
    UploadModal,
    CustomMessage,
  },
  computed: {
    ...mapGetters([
      'app',
    ]),
  },
  data() {
    return {
      dataLoading: false,
      checkedAll: false,
      searchCondition: [
        {
          key: 'resellerSubMerchantId',
          name: 'message.alert.resellerSubMerchantId',
          type: 'input',
          defValue: '',
          placeholder: 'message.alert.resellerSubMerchantIdInputTips',
        },
        {
          key: 'billDesc',
          name: 'message.alert.billDesc',
          type: 'input',
          defValue: '',
          placeholder: 'message.alert.billingDescriptorInputTips',
        },
        {
          key: ['alertTimeStart', 'alertTimeEnd'],
          name: 'message.alert.alertTime',
          defValue: ['', ''],
          type: 'date_time',
        },
        {
          key: ['txnTimeStart', 'txnTimeEnd'],
          name: 'message.alert.authTime',
          defValue: ['', ''],
          type: 'date_time',
        },
        {
          key: 'outcomeList',
          name: 'message.alert.outcomeStatus',
          type: 'select',
          defValue: [],
          multiple: true,
          isInternationalization: true,
          options: [],
        },
        {
          key: 'refundStatusList',
          name: 'message.alert.refundStatus',
          type: 'select',
          defValue: [],
          multiple: true,
          isInternationalization: true,
          options: [],
        },
        {
          key: ['outcomeUpdatedTimeStart', 'outcomeUpdatedTimeEnd'],
          name: 'message.alert.outcomeUpdatedTime',
          defValue: ['', ''],
          type: 'date_time',
        },
      ],
      searchButtons: [
        {
          type: 'download',
          permissionCode: 'risk:ethoca:alerts:list:download',
          func: async (param) => {
            if (!this.downloading) {
              this.downloading = true;
              try {
                const res = await downloadEthocaAlertsList(param);
                if (res) {
                  window.location.href = res;
                  ShowSuccessMessage(this.$t('message.downloadSuccessfulTip'));
                }
              } catch (err) {
                //
              }
              this.downloading = false;
            }
          },
        },
        {
          type: 'other',
          name: 'message.common.import',
          buttonType: 'default',
          permissionCode: 'risk:ethoca:alerts:list:import',
          func: () => {
            this.$refs.uploadModal.openModal();
          },
        },
        {
          type: 'other',
          name: 'message.common.batchOperations',
          permissionCode: 'risk:ethoca:alerts:list:batchOperation',
          func: () => {
            const ids = this.$refs.table.getCheckedAllList('ethocaId', 'id');
            if (ids.length === 0) {
              ShowWarningMessage(this.$t('message.tableCommon.atSelectLeastOne'));
              return;
            }
            this.$refs.batchModal.openModalAndCallBack((form) => {
              form.setValidationSelectOptions('outcome', this.outcomeStatusList);
              form.setValidationSelectOptions('refundStatus', this.refundStatusList);
              this.$refs.batchModal.setModifyCondition({
                ethocaIds: ids,
              });
            });
          },
        },
      ],
      tableCondition: [
        {
          titleBar: {
            type: 'checkbox',
            title: 'message.alert.alertTime',
            checkbox: { checked: false },
          },
          key: 'id',
          type: 'checkbox',
          itemWidth: 80,
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.alertTime',
          },
          key: 'alertTime',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.alertAge',
          },
          key: 'alertAge',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.ethocaId',
          },
          key: 'ethocaId',
          type: 'text',
        },
        // {
        //   titleBar: {
        //     type: 'text',
        //     title: 'message.alert.alertType',
        //   },
        //   key: 'alertType',
        //   type: 'text',
        // },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.authTime',
          },
          key: 'txnTime',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.amount',
          },
          key: 'txnAmount',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.currency',
          },
          key: 'txnCurrency',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.resellerSubMerchantId',
          },
          key: 'resellerSubMerchantId',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.billDesc',
          },
          key: 'billDesc',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.arn',
          },
          key: 'arn',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.outcomeStatus',
          },
          key: 'outcome',
          type: 'text',
          textFunc: (data) => getEnantiomericValue(this.outcomeStatusList, data.outcome),
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.refundStatus',
          },
          key: 'refundStatus',
          type: 'text',
          textFunc: (data) => getEnantiomericValue(this.refundStatusList, data.refundStatus),
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.alert.outcomeUpdatedTime',
          },
          key: 'outcomeUpdatedTime',
          type: 'text',
        },
        {
          key: 'buttons',
          titleBar: {
            title: 'message.tableCommon.operation',
            type: 'operations',
          },
          type: 'operations',
          subTableDisplays: [
            {
              type: 'text_button',
              textFunc: () => 'message.tableCommon.details',
              permissionCode: 'risk:ethoca:alerts:list:see',
              func: (data) => {
                this.$router.push({
                  path: '/risk/ethoca/alerts/view',
                  query: {
                    id: data.id,
                  },
                });
              },
            },
            {
              type: 'text_button',
              textFunc: () => 'message.alert.outcome',
              permissionCode: 'risk:ethoca:alerts:list:outcome',
              func: (data) => {
                this.$refs.outcomeModal.openModalAndCallBack((form) => {
                  form.setValidationSelectOptions('outcome', this.outcomeStatusList);
                  form.setValidationSelectOptions('refundStatus', this.refundStatusList);
                  this.$refs.outcomeModal.setModifyCondition({
                    ethocaId: data.ethocaId,
                  });
                });
              },
            },
          ],
        },
      ],
      outcomeCondition: [{
        key: 'outcome',
        name: 'message.alert.outcome',
        type: 'select',
        defValue: '',
        isShowStar: true,
        rule: [REQUIRED],
      }, {
        key: 'refundStatus',
        name: 'message.alert.refundStatus',
        type: 'select',
        defValue: '',
        isShowStar: true,
        rule: [REQUIRED],
      }],
      batchCondition: [{
        key: 'outcome',
        name: 'message.alert.outcome',
        type: 'select',
        defValue: '',
        isShowStar: true,
        rule: [REQUIRED],
      }, {
        key: 'refundStatus',
        name: 'message.alert.refundStatus',
        type: 'select',
        defValue: '',
        isShowStar: true,
        rule: [REQUIRED],
      }],
      outcomeStatusList: [],
      refundStatusList: [],
    };
  },
  mounted() {
    this.initMethod();
  },
  methods: {
    queryEthocaAlertList,
    async initMethod() {
      this.dataLoading = true;
      try {
        const dict = await GlobalData.getDictList(['ETHOCA_ACW_OUTCOME', 'ETHOCA_ACW_REFUND_STATUS']);
        this.outcomeStatusList = initSelectedData(dict.ETHOCA_ACW_OUTCOME, 'description', 'value', false);
        this.refundStatusList = initSelectedData(dict.ETHOCA_ACW_REFUND_STATUS, 'description', 'value', false);
        this.$refs.search.setSearchSelectOptions('outcomeList', this.outcomeStatusList);
        this.$refs.search.setSearchSelectOptions('refundStatusList', this.refundStatusList);
        if (this.$refs.table) {
          this.$refs.table.firstVisitHttp();
        }
      } catch (err) {
        //
      }
      this.dataLoading = false;
    },
    refreshTable() {
      this.$refs.table.refreshTable();
    },
    async onShowTips(ethocaIds) {
      return await new Promise((resolve, reject) => {
        const tableList = this.$refs.table.getTableData();
        let existCount = 0;
        for (let i = 0; i < tableList.length; i++) {
          const item = tableList[i].ethocaId.itemData;
          if (ethocaIds.includes(item.ethocaId)) {
            if (item.outcome || item.refundStatus) {
              existCount += 1;
            }
          }
        }
        this.$refs.customMessage.openDefaultModal({
          contentHtml: this.$t('message.alert.alertBatchOperateTips', { count: ethocaIds.length, duplicates: existCount }),
          titleHtml: this.$t('message.common.prompt'),
          onCallback: (action) => {
            if (action === 'confirm') {
              resolve();
            } else {
              reject(new Error(''));
            }
          },
        }, '');
      });
    },
    async onBatchOperation(params) {
      try {
        await this.onShowTips(params.ethocaIds);
        const data = [];
        for (let i = 0; i < params.ethocaIds.length; i++) {
          data.push({
            ethocaId: params.ethocaIds[i],
            outcome: params.outcome,
            refundStatus: params.refundStatus,
          });
        }
        const res = await batchEthocaAlertOutcome(data);
        ShowSuccessMessage(this.$t('message.submitSuccessfulTip'));
        this.refreshTable();
        return res;
      } catch (err) {
        //
      }
      return {};
    },
    async onOutcome(params) {
      const res = await outcomeEthocaAlert(params);
      return res;
    },
    async onUploadFile(params) {
      const res = await batchImportEthocaAlerts(params);
      return res;
    },
  },
};
</script>
<style lang="scss">
</style>
